/** Push notification campaign model. */
export type NotificationCampaignType =
  | 'OFFER'
  | 'DISCOUNT'
  | 'SERVICE'
  | 'REMINDER'
  | 'AI'
  | 'BLOG'
  | 'ALERT'
  | 'AWARENESS';

/** App notification navigation type (matches UserNotificationHandler / VendorNotificationHandler). */
export type AppNotificationIntentType = string;

export type TargetAudience = 'ALL_USERS' | 'VENDORS' | 'DELIVERY_PARTNERS' | 'UNREGISTERED_GUESTS' | 'SINGLE_USER' | 'INACTIVE_USERS' | 'USERS_WITHOUT_HEALTH_RECORD' | 'LOGGED_OUT_USERS' | 'DOCTORS' | 'AMBULANCE' | 'INACTIVE_VENDORS' | 'LOGGED_OUT_VENDORS' | 'PENDING_APPROVAL_VENDORS' | 'INACTIVE_DOCTORS' | 'PENDING_APPROVAL_DOCTORS' | 'INACTIVE_AMBULANCE' | 'PENDING_APPROVAL_AMBULANCE' | 'LOGGED_OUT_DOCTORS' | 'LOGGED_OUT_AMBULANCE' | 'SINGLE_VENDOR' | 'SINGLE_DOCTOR' | 'SINGLE_AMBULANCE';

/** ANDROID | IOS | WEB | BOTH | comma list e.g. ANDROID,IOS,WEB */
export type TargetPlatform = 'ANDROID' | 'IOS' | 'WEB' | 'BOTH' | string;

export function formatTargetPlatformLabel(p: string | null | undefined): string {
  const key = String(p || 'BOTH').toUpperCase();
  if (key.includes(',')) {
    return key
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => {
        if (x === 'IOS') return 'iOS';
        if (x === 'ANDROID') return 'Android';
        if (x === 'WEB') return 'Web';
        return x;
      })
      .join(' + ');
  }
  const m: Record<string, string> = {
    ANDROID: 'Android',
    IOS: 'iOS',
    WEB: 'Web',
    BOTH: 'Android & iOS',
  };
  return m[key] ?? key;
}

export type CampaignStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'SENDING'
  | 'RUNNING'
  | 'PAUSED'
  | 'SENT'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'FAILED';

export type ScheduleType = 'NOW' | 'ONCE' | 'DAILY' | 'WEEKLY' | 'EVERY_15_DAYS' | 'MONTHLY';

export type ConditionTree = {
  combinator?: 'AND' | 'OR';
  rules?: ConditionTree[];
  field?: string;
  operator?: string;
  value?: string | number | boolean;
};

export type ConditionFieldOption = {
  field: string;
  label: string;
  valueType: string;
  operators: string[];
  expensive?: boolean;
  note?: string | null;
};

export type PushCampaignExecution = {
  id: string;
  campaignId: string;
  triggerType: string;
  status: string;
  scheduledFor: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  timezone: string | null;
  eligibleCount: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  error: string | null;
  createdAt: string;
};
export interface PushNotificationCampaign {
  id: string;
  title: string;
  message: string;
  imageUrl: string | null;
  bannerImageUrl?: string | null;
  notificationType: NotificationCampaignType;
  intentType: AppNotificationIntentType;
  intentValue: string | null;
  targetAudience: TargetAudience;
  /** UUID of a specific user if audience is SINGLE_USER. */
  targetUserId?: string | null;
  /** ANDROID | IOS | WEB | BOTH — may be absent on older API responses */
  targetPlatform?: TargetPlatform;
  filterCity: string | null;
  filterState: string | null;
  status: CampaignStatus;
  scheduledAt: string | null;
  /** IANA timezone for scheduledAt wall clock (e.g. Asia/Kolkata). */
  scheduleTimezone?: string | null;
  sentAt: string | null;
  lastSendMeta: {
    platforms?: string[];
    estimatedRecipients?: number;
    tokensTargeted?: number;
    fcmSuccessCount?: number;
    fcmFailureCount?: number;
    sentAt?: string;
    mock?: boolean;
  } | null;
  /** Cumulative FCM successes across all sends (DB). */
  totalFcmDelivered?: number;
  /** Cumulative FCM failures across all sends (DB). */
  totalFcmFailed?: number;
  /** Number of send operations (including resend). */
  totalSendAttempts?: number;
  lastFcmSuccessCount?: number | null;
  lastFcmFailureCount?: number | null;
  lastTokensTargeted?: number | null;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
  conditionTree?: ConditionTree | null;
  scheduleType?: ScheduleType | string | null;
  scheduleConfig?: Record<string, unknown> | null;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  templateId?: string | null;
  /** Future: key from user behaviour analysis segments. */
  behaviourSegmentKey?: string | null;
}

export interface CampaignListResponse {
  success: boolean;
  data: {
    items: PushNotificationCampaign[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  };
}
